package dev.lingvo.shared.screenshots.templates

import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.screenshots.doc.BackgroundLayer
import dev.lingvo.shared.screenshots.doc.Board
import dev.lingvo.shared.screenshots.doc.Box
import dev.lingvo.shared.screenshots.doc.BrandKit
import dev.lingvo.shared.screenshots.doc.CaptionAt
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.FitSpec
import dev.lingvo.shared.screenshots.doc.FrameKind
import dev.lingvo.shared.screenshots.doc.LayerBase
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.Palette
import dev.lingvo.shared.screenshots.doc.Paint
import dev.lingvo.shared.screenshots.doc.Role
import dev.lingvo.shared.screenshots.doc.SceneKind
import dev.lingvo.shared.screenshots.doc.SceneSpec
import dev.lingvo.shared.screenshots.doc.TemplateId
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.doc.TextStyle
import dev.lingvo.shared.screenshots.doc.TypeRole
import dev.lingvo.shared.screenshots.doc.VAlign
import dev.lingvo.shared.screenshots.frames.VectorFrames
import dev.lingvo.shared.screenshots.geom.safeArea
import dev.lingvo.shared.screenshots.scene.SceneInput
import dev.lingvo.shared.screenshots.scene.Scenes
import dev.lingvo.shared.screenshots.scene.namespaceDigest
import kotlin.math.round

/** Three restrained compositions provide a useful first export without requiring a canvas editor. */
object LaunchLayouts {
    /** A top caption leaves the app screen as the main proof of the claim. */
    val captionTop: LaunchLayout = LaunchLayout("layout-caption-top", "Caption above", SceneSpec(SceneKind.SINGLE))
    /** A bottom caption gives a closing message beneath a device that deliberately bleeds above the canvas. */
    val captionBottom: LaunchLayout = LaunchLayout("layout-caption-bottom", "Caption below", SceneSpec(SceneKind.SINGLE, captionAt = CaptionAt.BOTTOM))
    /** A scrim separates the low caption from a deliberately large device. */
    val captionOver: LaunchLayout = LaunchLayout("layout-caption-over", "Caption over device", SceneSpec(SceneKind.CAPTION_OVER_DEVICE))
    /** Keeps the picker and catalog in the launch order. */
    val all: List<LaunchLayout> = listOf(captionTop, captionBottom, captionOver)
}

/** Exposes a frameless choice without changing the stable Template builder contract. */
class LaunchLayout internal constructor(id: String, label: String, private val scene: SceneSpec) : Template {
    /** IDs are persisted in ordinary design documents. */
    override val id: TemplateId = TemplateId(id)
    /** Labels describe caption position in the picker. */
    override val displayName: String = label
    /** Layouts are kept separate from more expressive templates. */
    override val categories: Set<TemplateCategory> = setOf(TemplateCategory.LAYOUT)
    /** A green seed avoids the generic purple-blue starter gradient. */
    override val defaultHue: Double = 145.0
    /** Script fallback can replace this Latin display family. */
    override val families: Map<TypeRole, String> = mapOf(TypeRole.DISPLAY to "Inter Tight")
    /** START alignment follows the locale when positions mirror. */
    override val mirrorRtl: Boolean = true

    /** Play begins without hardware decoration, following its screenshot advice. */
    override fun build(brand: BrandKit, board: Board, slots: List<TemplateSlot>, palette: Palette): List<Page> =
        build(brand, board, slots, palette, frameless = DeviceClass.fromId(board.deviceClass)?.platform == StorePlatform.GOOGLE_PLAY)

    /** Gives callers an explicit frame preference while retaining the device class's default silhouette. */
    fun build(brand: BrandKit, board: Board, slots: List<TemplateSlot>, palette: Palette, frameless: Boolean): List<Page> {
        if (board.deviceClass == DeviceClass.PLAY_FEATURE_GRAPHIC.id) {
            return featureGraphicPages(brand, board, slots, families.getValue(TypeRole.DISPLAY))
        }
        val frame = if (frameless) FrameKind.NONE else defaultFrame(board)
        return slots.mapIndexed { index, slot -> buildPage(board, slot, index, scene, frame, families.getValue(TypeRole.DISPLAY)) }
    }
}

internal fun halfPixel(value: Double): Double = round(value * 2) / 2
internal fun defaultFrame(board: Board): FrameKind = VectorFrames.defaultFor(requireNotNull(DeviceClass.fromId(board.deviceClass)))
internal fun templateFrame(board: Board): FrameKind =
    if (DeviceClass.fromId(board.deviceClass)?.platform == StorePlatform.GOOGLE_PLAY) FrameKind.NONE else defaultFrame(board)

internal fun buildPage(board: Board, slot: TemplateSlot, index: Int, scene: SceneSpec, frame: FrameKind, family: String): Page {
    val w = board.canvas.width.toDouble()
    val h = board.canvas.height.toDouble()
    val id = if (board.id.length <= 19) "${board.id}-p$index"
        else "${namespaceDigest(board.id).take(24)}p$index"
    val page = Page(id, listOf(slot.slotKey), scene, listOf(
        BackgroundLayer(LayerBase("$id-bg", box = Box(0.0, 0.0, w, h)), Paint.Solid("@bg")),
        DeviceLayer(LayerBase("$id-device", box = Box(0.0, 0.0, w, h), role = Role.DEVICE_1), frame),
        TextLayer(LayerBase("$id-caption", box = Box(0.0, 0.0, w, h), role = Role.CAPTION), TextContent.SlotCaption(),
            TextStyle(family = family, color = Paint.Solid("@ink"), vAlign = when {
                w > h -> VAlign.MIDDLE
                scene.kind == SceneKind.CAPTION_OVER_DEVICE -> VAlign.BOTTOM
                else -> VAlign.TOP
            }), FitSpec(halfPixel(.090 * w), halfPixel(.042 * w), 3)),
    ))
    return Scenes.apply(page, SceneInput(board.canvas, 1, safeArea(board, defaultFrame(board)), scene, frame, h / w))
}
